import { modalService } from "@whatslly/ui-components/modals";
import styles from "./Modals.module.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { inject, injectable } from "inversify";
import { Button } from "@whatslly/ui-components/v2/Button";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import type { MicrosoftPurviewSettings } from "@whatslly/types/messages-server/destinations";
import { GenerateCertificateResponse, GenerateCertificateResponseSuccess, DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import pick from "lodash/pick";
import { observer } from "mobx-react-lite";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { parseOrReturnError } from "../../../services/utils";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { TFunction } from "i18next";
import { makeObservable, observable, action } from "mobx";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { MicrosoftGraphAuthBodyResponse } from "@whatslly/types/integrations/MicrosoftGraphAuth";
import { DestinationsStore } from "../DestinationsStore";
import type { DestinationFormProps } from "../utils/DestinationsListTypes";
@injectable()
class ServerToServerStore {
  generateCertificatePromise: IPromiseBasedObservable<GenerateCertificateResponse | void> | undefined;
  generateServerToServerAccessTokenPromise: IPromiseBasedObservable<MicrosoftGraphAuthBodyResponse | void> | undefined;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      generateCertificatePromise: observable,
      generateServerToServerAccessTokenPromise: observable,
      generateCertificate: action,
      generateSererToServerAccessToken: action,
    });
  }

  async generateCertificate(t: TFunction) {
    this.generateCertificatePromise = fromPromise(
      getJSON({
        urlSuffix: `/message/destinations/${await this.orgStore.orgId}/generate-certificate`,
        etoken: this.userStore.etoken ?? "",
      })
        .then((r) => parseOrReturnError<GenerateCertificateResponse>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
    );
  }
  generateSererToServerAccessToken(
    t: TFunction,
    {
      privateKey,
      connectedAppConsumerKey,
      tenantId,
      certificate,
    }: {
      tenantId: string;
      connectedAppConsumerKey: string;
      privateKey: string;
      certificate: string;
    }
  ) {
    this.generateServerToServerAccessTokenPromise = fromPromise(
      (async () => {
        try {
          const res = await postJSON({
            urlSuffix: `/integrations/microsoft-graph/server-to-server`,
            body: JSON.stringify({
              privateKey,
              certificate,
              connectedAppConsumerKey,
              tenantId,
            }),
          }).then((r) => parseOrReturnError<MicrosoftGraphAuthBodyResponse>(r, this.logger));
          if (res.success) {
            return res;
          }
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        } catch {
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        }
      })()
    );
  }
  downloadCertificate(certificate: string | undefined) {
    if (certificate) {
      downloadBlob(atob(certificate), "tuvis-cert.pem", "application/x-pem-file");
      return;
    }
    if (this.generateCertificatePromise?.state === "fulfilled" && this.generateCertificatePromise.value?.success === true) {
      downloadBlob(atob(this.generateCertificatePromise.value.certificate), "tuvis-cert.pem", "application/x-pem-file");
    }
  }
}

type ServerToServerFormDataTypeFields = "connectedAppConsumerKey" | "tenantId" | "userId";
type ServerToServerFormDataType = Pick<MicrosoftPurviewSettings, ServerToServerFormDataTypeFields>;

export const MicrosoftPurviewModalForm: React.FC<DestinationFormProps<"microsoftPurviewDlpDetector">> = observer(({ onSubmitForm, settings }) => {
  const state = useLocalStoreWithInjectedDependencies(ServerToServerStore);
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const formikContext = useRef<FormikProps<ServerToServerFormDataType>>(null);
  const { t } = useTranslation();

  const validate = async (values: ServerToServerFormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof ServerToServerFormDataType, string>> = {};
    if (!values.connectedAppConsumerKey) errors.connectedAppConsumerKey = t("field_is_required");
    if (!values.tenantId) errors.tenantId = t("field_is_required");
    if (!values.userId) errors.userId = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const isTestSubmitDisabled = useMemo(() => {
    const certFromServer = state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise?.value?.success;
    const certFromSettings = settings?.certificate ?? "";
    return (!certFromServer && !certFromSettings) || formikErrorHasErrors;
  }, [state.generateCertificatePromise?.state, settings?.certificate, formikErrorHasErrors]);

  const getFormValues = () => {
    return (formikContext.current?.values ?? {}) as ServerToServerFormDataType;
  };

  const getCertData = useCallback(() => {
    return state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success
      ? pick<GenerateCertificateResponseSuccess, "privateKey" | "certificate">(state.generateCertificatePromise.value ?? {}, [
          "privateKey",
          "certificate",
        ])
      : {
          certificate: settings?.certificate ?? "",
          privateKey: settings?.privateKey ?? "",
        };
  }, [state.generateCertificatePromise?.state, state.generateCertificatePromise?.value, settings?.certificate, settings?.privateKey]);

  const getSubmitData = useCallback(() => {
    const certData = getCertData();
    return {
      type: "microsoftPurviewDlpDetector" as const,
      integrationType: DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS,
      settings: {
        accessToken:
          state.generateServerToServerAccessTokenPromise?.state === "fulfilled" && state.generateServerToServerAccessTokenPromise.value?.success
            ? state.generateServerToServerAccessTokenPromise.value.accessToken
            : "",
        ...getFormValues(),
        ...certData,
      },
    };
  }, [state.generateCertificatePromise?.state]);

  useEffect(() => {
    validate(getFormValues());
  }, []);

  const onSubmit = useCallback(async () => {
    state.generateSererToServerAccessToken(t, {
      ...getCertData(),
      ...getFormValues(),
    });
    const res = await state.generateServerToServerAccessTokenPromise;
    if (!res?.success) {
      return;
    }
    store.testConnection(t, getSubmitData());
    const resTestConnection = await store.testConnectionPromise;
    if (!resTestConnection?.success) {
      return;
    }
    onSubmitForm(getSubmitData());
  }, [onSubmitForm, getCertData, getSubmitData, state, store, t]);

  return (
    <div>
      <div className={styles.serverToServerOnly}>
        {settings?.certificate ||
        (state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success === true) ? (
          <Button testId={"download-certificate-button"} onClick={() => state.downloadCertificate(settings?.certificate)} paddingInline={"small"}>
            {t("destinations_page.download_certificate")}
          </Button>
        ) : (
          <Button
            testId={"generate-certificate-button"}
            isSubmitting={state.generateCertificatePromise?.state === "pending"}
            disabled={state.generateCertificatePromise?.state === "pending"}
            onClick={() => state.generateCertificate(t)}
            paddingInline={"small"}
          >
            {t("destinations_page.generate_certificate")}
          </Button>
        )}
      </div>
      <FormElement
        initialValues={
          settings
            ? pick<MicrosoftPurviewSettings, ServerToServerFormDataTypeFields>(settings, ["connectedAppConsumerKey", "tenantId", "userId"])
            : { connectedAppConsumerKey: "", tenantId: "", userId: "" }
        }
        validate={validate}
        onSubmit={onSubmit}
        formikRef={formikContext}
      >
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.client_id")} required />
          <FormTextInput name={"connectedAppConsumerKey"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.tenant_id")} required />
          <FormTextInput name={"tenantId"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.user_id")} required />
          <FormTextInput name={"userId"} paddingInline="none" />
        </div>
      </FormElement>
      <ModalButtons
        testId={`sf-save-button`}
        submitText={
          state.generateServerToServerAccessTokenPromise?.state === "pending" ? t("destinations_page.test_connection") : t("destinations_page.save")
        }
        isSubmitDisabled={isTestSubmitDisabled || state.generateServerToServerAccessTokenPromise?.state === "pending"}
        onSubmit={() => formikContext.current?.submitForm()}
        hideCancel={true}
      />
    </div>
  );
});
